import { ApiError } from '@/api/http';
import { fieldName, isIdentifier, readable } from '@/lib/vocabulary';

/**
 * Renders a payload the contract types as AnyDataEnvelope.
 *
 * Several packaging routes have no declared success schema, so there is no
 * generated type to render against. What is shown is still what the API
 * returned - nothing is reshaped or invented - but it is stated in English:
 * field names as an operator would say them, enums translated, dates local,
 * sizes human, and row identifiers omitted because a UUID tells an operator
 * nothing they can act on.
 *
 * Arrays are expanded row by row rather than counted: an array of tools IS
 * the breakdown, and collapsing it to "8 entries" hides the numbers the page
 * exists to show. A read that failed says so, and is never rendered as an
 * absence of facts.
 */

function Row({ item }: { item: unknown }) {
  if (typeof item !== 'object' || item === null)
    return <span className="text-caption text-ink">{readable('', item)}</span>;
  const fields = Object.entries(item as Record<string, unknown>).filter(
    ([k, v]) => (typeof v !== 'object' || v === null) && !isIdentifier(k, v),
  );
  if (fields.length === 0) return null;
  return (
    <span className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 text-caption text-ink">
      {fields.map(([k, v]) => (
        <span key={k}>
          <span className="text-ink-3">{fieldName(k)}</span>{' '}
          <span className="mono">{readable(k, v)}</span>
        </span>
      ))}
    </span>
  );
}

export function Facts({ value, pending, error }: { value: unknown; pending?: boolean; error?: unknown }) {
  if (pending) return <p className="px-4 py-3 text-small text-ink-3">Reading this from the API.</p>;
  if (error)
    return (
      <p className="px-4 py-3 text-small text-warn">
        {error instanceof ApiError ? `${error.reason ?? error.message}` : 'The API did not respond.'}
      </p>
    );
  if (value === null || value === undefined) return <p className="px-4 py-3 text-small text-ink-3">Nothing recorded.</p>;
  if (typeof value !== 'object')
    return <p className="mono px-4 py-3 text-small text-ink-2">{readable('', value)}</p>;

  const entries = Object.entries(value as Record<string, unknown>).filter(([k, v]) => !isIdentifier(k, v));
  if (entries.length === 0) return <p className="px-4 py-3 text-small text-ink-3">Nothing recorded.</p>;

  return (
    <div>
      {entries.map(([k, v]) => (
        <div
          key={k}
          className="flex flex-col gap-1 border-b border-line px-4 py-1.5 text-small last:border-0 sm:flex-row sm:items-baseline sm:gap-3"
        >
          <span className="w-48 flex-none text-ink-2">{fieldName(k)}</span>
          <span className="min-w-0 flex-1 break-words">
            {Array.isArray(v) ? (
              v.length === 0 ? (
                <span className="text-caption text-ink-4">none</span>
              ) : (
                <span className="flex flex-col gap-0.5">
                  {v.map((item, i) => (
                    <Row key={i} item={item} />
                  ))}
                </span>
              )
            ) : typeof v === 'object' && v !== null ? (
              <Row item={v} />
            ) : (
              <span className="mono text-caption text-ink">{readable(k, v)}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
